import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2, Mail } from "lucide-react";
import api from "@/lib/api";
import { PlanDisplay, ScoreGauge } from "@/pages/WebAdvisorPage";
import { ProposalResponse } from "@/components/webadvisor/ProposalResponse";
import { PacketSection } from "@/components/webadvisor/PacketSection";
import { ShopAdvisorMark } from "@/components/webadvisor/InvoiceTable";
import { GrowthChart, SimTotals } from "@/components/webadvisor/GrowthChart";
import { money } from "@/lib/landingSim";

const PackageSection = ({ pkg }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="public-package">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-wider text-blue-600 font-bold">Visibility boost</p>
        <h2 className="text-xl font-black text-slate-900">{pkg.page_count} SEO landing pages, built &amp; hosted</h2>
        <p className="text-sm text-slate-500">{money(pkg.unit_price)} per page{pkg.discount > 0 ? ` · ${pkg.discount_pct}% discount (−${money(pkg.discount)})` : ""}</p>
      </div>
      <p className="text-3xl font-black text-blue-700" data-testid="public-package-total">{money(pkg.total)}</p>
    </div>
    {pkg.simulation && <>
      <p className="font-semibold text-slate-900">Projected traffic growth <span className="text-sm font-normal text-slate-500">({pkg.growth_rate_pct}% monthly growth)</span></p>
      <GrowthChart sim={pkg.simulation} />
      <SimTotals sim={pkg.simulation} cost={pkg.total} />
      <p className="text-xs text-slate-400">Projection only — actual results vary.</p>
    </>}
  </div>
);

export default function PublicProposalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/api/webadvisor/public/proposal/${token}`).then(r => setData(r.data)).catch(() => setError(true));
  }, [token]);
  const reload = useCallback(() => { api.get(`/api/webadvisor/public/proposal/${token}`).then(r => setData(r.data)).catch(() => {}); }, [token]);
  const loaded = !!data;
  useEffect(() => { if (loaded && window.location.hash) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }), 300); }, [loaded]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="public-proposal-missing">This proposal is no longer available.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;

  const { audit, plan, contact, package: pkg } = data;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webadvisor/public/proposal/${token}/pdf`;
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6" data-testid="public-proposal-page">
      <div className="max-w-5xl mx-auto space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm flex flex-col sm:flex-row items-center gap-6">
          <ScoreGauge score={audit.scores?.overall_score || 0} size={130} />
          <div className="flex-1 text-center sm:text-left">
            <p className="text-xs uppercase tracking-wider text-blue-600 font-bold">Website Growth Proposal</p>
            <h1 className="text-3xl font-black text-slate-900 break-all">{audit.business_name || audit.url.replace(/^https?:\/\//, "")}</h1>
            {audit.business_name && <p className="text-sm text-slate-500 break-all">{audit.url}</p>}
          </div>
          <div className="flex flex-col gap-2">
            {plan.prospect_response?.action !== "accepted" && <a href="#respond" className="bg-green-600 hover:bg-green-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg text-center" data-testid="public-proposal-respond-link">Accept or respond</a>}
            <a href={pdfUrl} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2" data-testid="public-proposal-pdf-btn"><Download className="w-4 h-4" />Download PDF</a>
          </div>
        </div>
        <PlanDisplay plan={plan} audit={audit} invoice={data.invoice} />
        {pkg && <PackageSection pkg={pkg} />}
        <ProposalResponse token={token} plan={plan} invoice={data.invoice} signature={data.signature} onChange={reload} />
        {data.invoice?.total > 0 && <PacketSection token={token} packet={data.packet} invoice={data.invoice} signature={data.signature} countersignature={data.countersignature} accepted={plan.prospect_response?.action === "accepted"} />}
        {contact?.email && (
          <div className="bg-slate-900 text-white rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="public-proposal-contact">
            <div className="text-center sm:text-left">
              <p className="text-lg font-bold">Ready to grow your website?</p>
              <p className="text-sm text-slate-300">Reply to {contact.name || "your advisor"} to get started or ask questions.</p>
            </div>
            <a href={`mailto:${contact.email}?subject=${encodeURIComponent(`Growth proposal — ${audit.business_name || audit.url}`)}`} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-5 py-2.5 rounded-lg flex items-center gap-2" data-testid="public-proposal-contact-btn"><Mail className="w-4 h-4" />Contact {contact.name?.split(" ")[0] || "us"}</a>
          </div>
        )}
        <ShopAdvisorMark />
      </div>
    </div>
  );
}
